import "zod-openapi/extend";

import { z } from "zod";
import {
  ContentInputSchema,
  ContentOutputSchema,
  ContentSchema,
} from "block-system/types/blocks";
import {
  ALPHANUMERIC_DASHES_SPACES_REGEX,
  PAGE_SLUG_REGEX,
  URL_PATTERN_REGEX,
} from "utils/regex";

export const MAX_BLOCKS_PER_PAGE = 80;

export const pageNameSchema = z
  .string()
  .min(1, "Name is required")
  .max(32)
  .regex(ALPHANUMERIC_DASHES_SPACES_REGEX, "Name contains invalid characters")
  .describe(
    "The name of the page. The name of the page is displayed in the page editor navigation menu."
  );

export const pageTitleSchema = z
  .string()
  .min(1, "Title should not be empty")
  .max(32);

export const pageSlugSchema = z
  .string()
  .min(1, "Required")
  .max(32)
  .regex(PAGE_SLUG_REGEX, "Must be lowercase and alphanumeric or dashes")
  .describe(
    "The slug of the page. The slug is the part of the URL that appears after the domain name. It is used to identify the page and is typically used in the in-page navigation menu."
  );

export const PageWithoutContentSchema = z
  .object({
    id: z.string().cuid(),
    name: z.string(),
    title: z.string(),
    navigationEnabled: z.boolean().optional().default(true),
    slug: z.string(),
    projectId: z.string().cuid(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  })
  .openapi({ ref: "InterfacesPage" });

export const PageSchema = PageWithoutContentSchema.extend({
  content: ContentSchema,
});

export const createPageSchema = z.object({
  name: pageNameSchema,
  title: pageTitleSchema,
  projectId: z.string().cuid(),
  slug: pageSlugSchema.optional(),
  navigationEnabled: z
    .boolean()
    .optional()
    .default(true)
    .openapi({ effectType: "input" }),
});

export const UpdatePageInputSchema = z
  .object({
    name: pageNameSchema.optional(),
    title: z
      .string()
      .min(1, "Title should not be empty")
      .max(32)
      .optional()
      .describe(
        "The title of the page. The title will be displayed as the title of the browser tab and used as links in the in-page navigation menu."
      ),
    slug: pageSlugSchema.optional(),
    navigationEnabled: z
      .boolean()
      .optional()
      .default(true)
      .describe("Whether to display the global navigation menu on this page."),
    content: ContentInputSchema.optional(),
  })
  .openapi({ ref: "UpdateInterfacesPageInput" });

export const getPageSchema = z.object({
  id: z.string().cuid(),
});

export const PagePublicOutputSchema = z
  .object({
    id: z.string().cuid(),
    projectId: z.string().cuid(),
    name: z.string(),
    slug: z.string(),
    title: z.string(),
    navigationEnabled: z
      .boolean()
      .optional()
      .default(false)
      .openapi({ effectType: "output" }),
    content: ContentOutputSchema.default({
      version: "2",
      blocks: [],
    }).openapi({ effectType: "output" }),
    createdAt: z
      .date()
      .transform((val) => val.toISOString())
      .pipe(z.string().datetime()),
    updatedAt: z
      .date()
      .transform((val) => val.toISOString())
      .pipe(z.string().datetime()),
  })
  .openapi({ ref: "InterfacesPageWithContent" });

export const navigationPageItemSchema = z.object({
  id: z.string().cuid(),
  isHomepage: z.boolean(),
  title: z.string(),
  url: z.string(),
});

export type NavigationPageItem = z.infer<typeof navigationPageItemSchema>;

export const navigationPageLinkSchema = z
  .object({
    type: z.literal("page"),
    pageId: z.string(),
    opensInNewTab: z.boolean().optional(),
  })
  .strict();

export const navigationCustomLinkSchema = z
  .object({
    type: z.literal("url"),
    title: z.string().min(1, "Title is required"),
    url: z.string().regex(URL_PATTERN_REGEX, "Invalid URL"),
    opensInNewTab: z.boolean().optional(),
  })
  .strict();

export const navigationLinkSchema = z.discriminatedUnion("type", [
  navigationPageLinkSchema,
  navigationCustomLinkSchema,
]);
export const navigationLinksSchema = navigationLinkSchema.array();

export type NavigationLink = z.infer<typeof navigationLinkSchema>;
export type NavigationLinks = NavigationLink[];
export type PagePublicSchema = z.infer<typeof PagePublicOutputSchema>;
